import {
  AdminAnalyticsOverview,
  AnalyticsCountAgg,
  AnalyticsMixPoint,
} from '@/client/types/analytics';
import {
  TrendPeriod,
  DateRange,
  TrendPoint,
  MixItem,
  OverviewKpis,
  buildBucketStarts,
  addPeriod,
  formatBucketLabel,
  periodDelta,
  sumField,
} from './dashboard-trends';

const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

/** First element of a Mongo count aggregate array (`[{total,...}]` or `[]`). */
const firstAgg = (arr?: AnalyticsCountAgg[]): AnalyticsCountAgg => arr?.[0] ?? {};

const emptyPoint = (label: string, key: string): TrendPoint => ({
  label,
  key,
  acquired: 0,
  signups: 0,
  logins: 0,
  failedLogins: 0,
  adminLogins: 0,
  adminFailedLogins: 0,
  sessions: 0,
  churned: 0,
  engagements: 0,
  activeUsers: 0,
  listings: 0,
  listingsNew: 0,
  listingsActive: 0,
  dau: 0,
  mau: 0,
  newActive: 0,
  visitors: 0,
  visitors30d: 0,
  newVisitors: 0,
  membersNew: 0,
  membersReturning: 0,
  visitorsNew: 0,
  visitorsReturning: 0,
  ordersPaid: 0,
  gmv: 0,
  collected: 0,
  payCollected: 0,
  payPending: 0,
  payCanceled: 0,
  inbound: 0,
  replied: 0,
  replyLt5m: 0,
  replyLt30m: 0,
  replyLt2h: 0,
  replyLt12h: 0,
  replyLt48h: 0,
  replyGte48h: 0,
  subsStarted: 0,
  subsEnded: 0,
  subsActive: 0,
  subsPastDue: null,
});

/** The currency the money tiles and charts are shown in: the one with the most in it. */
const mainOrderCcode = (raw?: AdminAnalyticsOverview) => raw?.summary?.paid_orders?.[0]?.ccode ?? 'GHS';
const mainPayment = (raw?: AdminAnalyticsOverview) =>
  [...(raw?.summary?.payments ?? [])].sort((a, b) => num(b.collected) - num(a.collected))[0];

/** A level series keyed by day, ascending, so a bucket can close on its last day. */
const levelDays = <T extends { date: string }>(arr?: T[]) =>
  [...(arr ?? [])].sort((a, b) => a.date.localeCompare(b.date));

const round1 = (n: number): number => Math.round(n * 10) / 10;

/** Flow fields `overviewToSeries` sums into a bucket with no special handling. */
const BUCKETED_FLOWS = [
  'adminLogins',
  'adminFailedLogins',
  'membersNew',
  'membersReturning',
  'visitorsNew',
  'visitorsReturning',
  'ordersPaid',
  'gmv',
  'collected',
  'payCollected',
  'payPending',
  'payCanceled',
  'inbound',
  'replied',
  'replyLt5m',
  'replyLt30m',
  'replyLt2h',
  'replyLt12h',
  'replyLt48h',
  'replyGte48h',
  'subsStarted',
  'subsEnded',
] as const satisfies readonly (keyof TrendPoint)[];

/**
 * Merge the API's daily trend arrays into `TrendPoint[]`, re-bucketed to the
 * selected period so the Dashboard's period/cumulative controls keep working.
 * Series the backend hasn't started emitting (sessions) stay at 0 until they
 * arrive.
 */
export function overviewToSeries(
  raw: AdminAnalyticsOverview | undefined,
  period: TrendPeriod,
  range: DateRange,
): TrendPoint[] {
  const byDate = new Map<string, Partial<TrendPoint>>();
  const add = (date: string | undefined, patch: Partial<TrendPoint>) => {
    if (!date) return;
    const cur = byDate.get(date) ?? {};
    for (const [k, v] of Object.entries(patch)) {
      (cur as any)[k] = num((cur as any)[k]) + num(v);
    }
    byDate.set(date, cur);
  };

  (raw?.charts?.signup_attempts_trends ?? []).forEach((p) =>
    add(p.date, { signups: num(p.successful ?? p.total) }),
  );
  (raw?.charts?.login_attempts_trends ?? []).forEach((p) =>
    add(p.date, {
      logins: num(p.successful),
      failedLogins: num(p.failed),
      adminLogins: num(p.admin_successful),
      adminFailedLogins: num(p.admin_failed),
    }),
  );
  (raw?.charts?.engagements ?? []).forEach((p) => add(p.date, { engagements: num(p.count) }));
  (raw?.charts?.listings ?? []).forEach((p) =>
    add(p.date, { listings: num(p.count), listingsNew: num(p.count) }),
  );
  (raw?.charts?.user_acquisition_trends ?? raw?.charts?.acquired_trends ?? []).forEach((p) =>
    add(p.date, { acquired: num(p.count ?? p.total) }),
  );
  (raw?.charts?.user_churn_trends ?? []).forEach((p) =>
    add(p.date, { churned: num(p.count ?? p.total) }),
  );
  // Order counts cover every currency, like the tile; amounts only the main one.
  const orderCcode = mainOrderCcode(raw);
  (raw?.charts?.paid_orders_trends ?? []).forEach((p) =>
    add(p.date, { ordersPaid: num(p.count), gmv: p.ccode === orderCcode ? num(p.amount) : 0 }),
  );
  const payCcode = mainPayment(raw)?.ccode ?? 'GHS';
  (raw?.charts?.payments_trends ?? [])
    .filter((p) => p.ccode === payCcode)
    .forEach((p) =>
      add(p.date, {
        collected: num(p.collected),
        payCollected: num(p.collected_count),
        payPending: num(p.pending_count),
        payCanceled: num(p.canceled_count),
      }),
    );
  (raw?.charts?.reply_trends ?? []).forEach((p) =>
    add(p.date, {
      inbound: num(p.inbound),
      replied: num(p.replied),
      replyLt5m: num(p.latency?.lt5m),
      replyLt30m: num(p.latency?.lt30m),
      replyLt2h: num(p.latency?.lt2h),
      replyLt12h: num(p.latency?.lt12h),
      replyLt48h: num(p.latency?.lt48h),
      replyGte48h: num(p.latency?.gte48h),
    }),
  );
  (raw?.charts?.subscriptions_trends ?? []).forEach((p) =>
    add(p.date, { subsStarted: num(p.started), subsEnded: num(p.ended) }),
  );
  (raw?.charts?.active_users_trends ?? []).forEach((p) =>
    add(p.date, {
      membersNew: num(p.first_time),
      membersReturning: Math.max(0, num(p.dau) - num(p.first_time)),
      visitorsNew: num(p.visitors_first_time),
      visitorsReturning: Math.max(0, num(p.visitors) - num(p.visitors_first_time)),
    }),
  );
  const subsDays = levelDays(raw?.charts?.subscriptions_trends);
  const listingDays = levelDays(raw?.charts?.active_listings_trends);

  // Ascending, so the last day seen in a bucket is its closing 30-day level.
  const activeDays = [...(raw?.charts?.active_users_trends ?? [])].sort((a, b) =>
    a.date.localeCompare(b.date),
  );

  const starts = buildBucketStarts(period, range);
  return starts.map((start) => {
    const next = addPeriod(start, period, 1);
    const point = emptyPoint(formatBucketLabel(period, start), start.toISOString().slice(0, 10));
    // Distinct counts do not add up across days: a bucket shows the average
    // day, and the trailing-30-day level it closed on.
    let days = 0;
    for (const p of activeDays) {
      const d = new Date(`${p.date}T00:00:00`);
      if (Number.isNaN(d.getTime()) || d < start || d >= next) continue;
      days += 1;
      point.dau += num(p.dau);
      point.visitors += num(p.visitors);
      point.mau = num(p.mau);
      point.visitors30d = num(p.visitors_30d);
      point.newActive += num(p.new_active);
      point.newVisitors += num(p.new_visitors);
    }
    if (days > 0) {
      point.dau = round1(point.dau / days);
      point.visitors = round1(point.visitors / days);
    }
    const inBucket = (date: string) => {
      const d = new Date(`${date}T00:00:00`);
      return d >= start && d < next;
    };
    for (const p of subsDays) {
      if (!inBucket(p.date)) continue;
      point.subsActive = num(p.active);
      point.subsPastDue = typeof p.past_due === 'number' ? p.past_due : null;
    }
    for (const p of listingDays) if (inBucket(p.date)) point.listingsActive = num(p.active);
    for (const [date, patch] of byDate) {
      const d = new Date(`${date}T00:00:00`);
      if (Number.isNaN(d.getTime()) || d < start || d >= next) continue;
      point.acquired += num(patch.acquired);
      point.churned += num(patch.churned);
      point.signups += num(patch.signups);
      point.logins += num(patch.logins);
      point.failedLogins += num(patch.failedLogins);
      point.engagements += num(patch.engagements);
      point.listings += num(patch.listings);
      point.listingsNew += num(patch.listingsNew);
      for (const f of BUCKETED_FLOWS) point[f] += num(patch[f]);
    }
    return point;
  });
}

/**
 * KPI figures from the summary block + the (already bucketed) series for deltas.
 * Missing fields fall back to 0; `acquired`, `active_users`, `sessions`, `orgs`,
 * `churn_rate` are the ones still being added on the backend.
 */
export function overviewToKpis(
  raw: AdminAnalyticsOverview | undefined,
  series: TrendPoint[],
): OverviewKpis {
  const s = raw?.summary ?? {};
  const login = firstAgg(s.login_count);
  const signup = firstAgg(s.signup_count);

  const totalUsers = num(s.users);
  const activeUsers = num(s.active_users);
  const churnedUsers = num(s.churned_users);
  const inactiveUsers = num(s.inactive_users) || churnedUsers;
  // Prefer an explicit rate; else derive from the churned-user count. Avoid the
  // `users - active_users` fallback while `active_users` is still 0 on the
  // backend — it would report a misleading ~100% churn.
  const churnRate =
    num(s.churn_rate) || (totalUsers > 0 ? churnedUsers / totalUsers : 0);
  const activeListings = num(s.listings);
  const newListings = sumField(series, 'listingsNew');

  const half = Math.floor(series.length / 2);
  const recent = series.slice(half);
  const prior = series.slice(0, half);

  return {
    totalUsers,
    activeUsers,
    signups: num(signup.successful ?? signup.total),
    logins: num(login.successful),
    failedLogins: num(login.failed),
    sessions: num(s.sessions),
    acquired: num(s.acquired_users ?? s.acquired),
    churnRate,
    inactiveUsers,
    activeListings,
    totalListings: activeListings,
    newListings,
    engagements: num(s.engagements),
    orgs: num(s.orgs),
    dau: num(s.dau),
    dauAvg: num(s.dau_avg),
    mau: num(s.mau),
    dailyVisitors: num(s.daily_visitors),
    monthlyVisitors: num(s.monthly_visitors),
    dauDelta: periodDelta(sumField(recent, 'dau'), sumField(prior, 'dau')),
    signupDelta: periodDelta(sumField(recent, 'signups'), sumField(prior, 'signups')),
    loginDelta: periodDelta(sumField(recent, 'logins'), sumField(prior, 'logins')),
    failedLoginDelta: periodDelta(sumField(recent, 'failedLogins'), sumField(prior, 'failedLogins')),
    acquiredDelta: periodDelta(sumField(recent, 'acquired'), sumField(prior, 'acquired')),
    churnDelta: periodDelta(sumField(recent, 'churned'), sumField(prior, 'churned')),
    engagementDelta: periodDelta(sumField(recent, 'engagements'), sumField(prior, 'engagements')),
    usersDelta: { pct: 0, direction: 'flat' },
    listingsDelta: periodDelta(sumField(recent, 'listingsNew'), sumField(prior, 'listingsNew')),
  };
}

/**
 * Money and marketplace-outcome figures from the summary block. Amount tiles
 * show a single currency: the one with the most money in it.
 */
export function overviewToCommerce(raw: AdminAnalyticsOverview | undefined) {
  const s = raw?.summary ?? {};
  const checks = firstAgg(s.password_checks);
  const orders = s.paid_orders ?? [];
  const pay = mainPayment(raw);
  const attempts = num(pay?.collected_count) + num(pay?.pending_count) + num(pay?.canceled_count);
  const subs = s.subscriptions ?? {};
  const payouts = Object.values(s.payouts ?? {});
  const inbound = num(s.reply_rate?.inbound);
  const live = num(s.inquired_listings?.live);
  return {
    passwordChecks: num(checks.total),
    passwordChecksFailed: num(checks.failed),
    paidOrders: orders.reduce((n, o) => n + num(o.count), 0),
    gmv: num(orders[0]?.amount),
    gmvCcode: orders[0]?.ccode ?? 'GHS',
    collected: num(pay?.collected),
    collectedCount: num(pay?.collected_count),
    collectedCcode: pay?.ccode ?? 'GHS',
    pendingPayments: num(pay?.pending_count),
    canceledPayments: num(pay?.canceled_count),
    /** Share of payment attempts that were collected; null with no attempts. */
    paymentSuccess: attempts > 0 ? num(pay?.collected_count) / attempts : null,
    activeSubs: num(subs.active?.count) + num(subs.trialing?.count),
    pastDueSubs: num(subs.past_due?.count),
    openPayouts: payouts.reduce((n, p) => n + num(p.count), 0),
    openPayoutAmount: payouts.reduce((n, p) => n + num(p.amount), 0),
    soldListings: num(s.sold_listings),
    liveListings: live,
    inquiredListings: num(s.inquired_listings?.inquired),
    inquiryDays: num(s.inquired_listings?.days) || 7,
    inquiredShare: live > 0 ? num(s.inquired_listings?.inquired) / live : null,
    inbound,
    replied: num(s.reply_rate?.replied),
    replyRate: inbound > 0 ? num(s.reply_rate?.replied) / inbound : null,
    /** Distinct visitors in the range: each is first seen in it exactly once. */
    rangeVisitors: (raw?.charts?.active_users_trends ?? []).reduce(
      (n, p) => n + num(p.new_visitors),
      0,
    ),
    inquiredInRange: num(s.inquired_in_range),
  };
}

export type TypeSeries = {
  /** One row per bucket: `label` plus a count per name in `names`. */
  rows: Record<string, number | string>[];
  names: string[];
};

const OTHER = 'Other';

/**
 * New listings per bucket, split by listing type: the `top` biggest types in
 * the range and everything else as "Other".
 */
export function overviewToTypeSeries(
  raw: AdminAnalyticsOverview | undefined,
  period: TrendPeriod,
  range: DateRange,
  cumulative: boolean,
  top = 6,
): TypeSeries {
  const points = raw?.charts?.listing_type_trends ?? [];
  const totals = new Map<string, number>();
  points.forEach((p) => totals.set(p.name, (totals.get(p.name) ?? 0) + num(p.count)));
  const ranked = [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([name]) => name);
  const kept = new Set(ranked.slice(0, top));
  const names = ranked.length > top ? [...kept, OTHER] : [...kept];

  const running: Record<string, number> = {};
  const rows = buildBucketStarts(period, range).map((start) => {
    const next = addPeriod(start, period, 1);
    const row: Record<string, number | string> = { label: formatBucketLabel(period, start) };
    names.forEach((n) => (row[n] = cumulative ? (running[n] ?? 0) : 0));
    for (const p of points) {
      const d = new Date(`${p.date}T00:00:00`);
      if (Number.isNaN(d.getTime()) || d < start || d >= next) continue;
      const name = kept.has(p.name) ? p.name : OTHER;
      row[name] = num(row[name]) + num(p.count);
    }
    names.forEach((n) => (running[n] = num(row[n])));
    return row;
  });
  return { rows, names };
}

const toMix = (arr?: AnalyticsMixPoint[]): MixItem[] =>
  (arr ?? []).map((r, i) => ({ key: `${r.name}-${i}`, label: r.name, count: num(r.count) }));

export function overviewToMix(raw: AdminAnalyticsOverview | undefined): {
  listingMix: MixItem[];
  engagementMix: MixItem[];
} {
  return {
    listingMix: toMix(raw?.line_charts?.listing_mix),
    engagementMix: toMix(raw?.line_charts?.engagement_mix),
  };
}
